import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiCheckSquare, FiMessageSquare, FiPaperclip, FiMic, FiStopCircle, FiEye, FiSave, FiX, FiCpu } from 'react-icons/fi';
import useEntity from '../hooks/useEntity';
import Modal from '../components/ui/Modal';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { puede } from '../utils/permisos';

const VACIO = { titulo: '', descripcion: '', urgencia: 'media', estado: 'pendiente', clienteId: '', asignadoA: '', fechaTope: '', diasPreaviso: '' };
const URGENCIAS = { baja: 'badge', media: 'badge-info', alta: 'badge-warning', critica: 'badge-danger' };
const ESTADOS = { pendiente: 'Pendiente', en_curso: 'En curso', completado: 'Completado' };

const AUDIO_EXTS = ['.mp3', '.wav', '.m4a', '.ogg', '.webm', '.aac', '.flac', '.mp4', '.opus'];
const esAudio = (a) => {
  if (!a) return false;
  if (a.tipo?.startsWith('audio/') || a.tipo?.includes('video/webm')) return true;
  const ext = (a.nombre || a.ruta || '').toLowerCase().match(/\.[^.]+$/)?.[0];
  return AUDIO_EXTS.includes(ext);
};

const MAX_MEDIA_BYTES = 25 * 1024 * 1024;
const MIME_MEDIA_PERMITIDOS = [
  'audio/webm', 'audio/wav', 'audio/x-wav', 'audio/wave', 'audio/mp3', 'audio/mpeg', 'audio/ogg', 'audio/m4a', 'audio/x-m4a', 'audio/mp4', 'audio/quicktime'
];
const MIME_POR_EXTENSION = {
  webm: 'audio/webm', wav: 'audio/wav', mp3: 'audio/mpeg', ogg: 'audio/ogg', m4a: 'audio/m4a', mp4: 'audio/mp4', mov: 'audio/quicktime'
};

function validarMedia(file, nombre) {
  if (!file) return { ok: false, error: 'Graba o selecciona un archivo de audio primero.' };
  if (file.size > MAX_MEDIA_BYTES) return { ok: false, error: 'El archivo supera el máximo permitido (25 MB).' };
  let mimeType = (file.type || '').split(';')[0].trim().toLowerCase();
  if (!MIME_MEDIA_PERMITIDOS.includes(mimeType)) mimeType = MIME_POR_EXTENSION[((nombre || '').split('.').pop() || '').toLowerCase()] || mimeType;
  if (!MIME_MEDIA_PERMITIDOS.includes(mimeType)) return { ok: false, error: 'Formato no soportado. Usa webm, wav, mp3, m4a, ogg o mp4 de audio.' };
  return { ok: true, mimeType };
}

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function TareaVoz({ onAplicar, onCerrar, clientes = [], equipo = [] }) {
  const [archivo, setArchivo] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [mediaUrl, setMediaUrl] = useState(null);
  const [grabando, setGrabando] = useState(false);
  const [modoGrabando, setModoGrabando] = useState('audio');
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [borrador, setBorrador] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const asignarArchivo = (file, nombre) => {
    if (!file) return;
    if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    setArchivo(file);
    setNombreArchivo(nombre);
    setMediaUrl(URL.createObjectURL(file));
    setError('');
    setBorrador(null);
  };

  const iniciarGrabacion = async () => {
    setError('');
    setModoGrabando('audio');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tipos = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg'];
      const mimeType = tipos.find((tipo) => window.MediaRecorder.isTypeSupported?.(tipo));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (evento) => { if (evento.data.size) chunksRef.current.push(evento.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        const tipoFinal = recorder.mimeType || 'audio/webm';
        asignarArchivo(new Blob(chunksRef.current, { type: tipoFinal }), `tarea-grabacion.webm`);
      };
      recorderRef.current = recorder;
      recorder.start();
      setGrabando(true);
    } catch (_err) {
      setError('No se pudo acceder al micrófono. Revisa los permisos.');
    }
  };

  const detenerGrabacion = () => {
    recorderRef.current?.stop();
    setGrabando(false);
  };

  const procesar = async () => {
    const validacion = validarMedia(archivo, nombreArchivo);
    if (!validacion.ok) { setError(validacion.error); return; }
    setCargando(true);
    setError('');
    try {
      setMensaje('Transcribiendo audio/vídeo...');
      const audio = await toBase64(archivo);
      const { data: transcripcion } = await api.post('/presupuestos-ia/transcribir-tarea', { audio, mimeType: validacion.mimeType });
      setMensaje('Extrayendo datos de la tarea con IA...');
      const { data } = await api.post('/presupuestos-ia/generar-tarea-desde-voz', { texto: transcripcion.texto });
      setBorrador(data);
      setMensaje('Revisa los datos antes de aplicarlos.');
    } catch (errProcesar) {
      setError(errProcesar?.response?.data?.error || errProcesar?.message || 'No se pudo procesar el archivo.');
    } finally {
      setCargando(false);
    }
  };

  const cerrar = () => {
    if (grabando) recorderRef.current?.stop();
    if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    onCerrar();
  };

  const coincidirCliente = (nombreBuscado) => {
    if (!nombreBuscado || !clientes.length) return '';
    const norm = nombreBuscado.toLowerCase().trim();
    const coincide = clientes.find((c) => c.nombre?.toLowerCase().includes(norm) || norm.includes(c.nombre?.toLowerCase()));
    return coincide?._id || '';
  };

  const coincidirUsuario = (nombreBuscado) => {
    if (!nombreBuscado || !equipo.length) return '';
    const norm = nombreBuscado.toLowerCase().trim();
    const coincide = equipo.find((u) => `${u.nombre} ${u.apellidos || ''}`.toLowerCase().includes(norm) || norm.includes(u.nombre?.toLowerCase()));
    return coincide?._id || '';
  };

  const aplicarBorrador = () => {
    if (!borrador?.datos) return;
    const d = borrador.datos;
    const clienteId = coincidirCliente(d.clienteNombre);
    const asignadoA = coincidirUsuario(d.asignadoNombre);

    onAplicar({
      titulo: d.titulo || 'Tarea de trabajo',
      descripcion: d.descripcion || '',
      urgencia: d.urgencia || 'media',
      estado: d.estado || 'pendiente',
      fechaTope: d.fechaTope || '',
      diasPreaviso: d.diasPreaviso != null ? d.diasPreaviso : '',
      asignadoA,
      clienteId,
    });
  };

  return (
    <div>
      <div className="alert-info" style={{ marginBottom: 14 }}>
        <strong>Qué debes decir:</strong><br />
        Debes describir el título de la tarea, la descripción, la urgencia, el cliente asociado, la fecha tope y, si procede, la persona asignada. Por ejemplo: <em>Tarea urgente para cliente Juan Pérez, revisa instalación eléctrica, prioridad alta, fecha tope 10 de septiembre, asignada a Ana García.</em>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {!grabando ? (
          <button type="button" className="btn" onClick={iniciarGrabacion} disabled={cargando}>
            <FiMic /> Grabar audio
          </button>
        ) : (
          <button type="button" className="btn btn-danger" onClick={detenerGrabacion}>
            <FiStopCircle /> Detener grabación ({modoGrabando})
          </button>
        )}
        <label className="btn">
          <FiPlus /> Subir audio
          <input type="file" accept="audio/*" hidden disabled={grabando || cargando} onChange={(e) => asignarArchivo(e.target.files?.[0], e.target.files?.[0]?.name)} />
        </label>
      </div>

      {mediaUrl ? (
        <audio controls src={mediaUrl} style={{ width: '100%', marginBottom: 12 }}>Tu navegador no soporta reproducción de audio.</audio>
      ) : null}

      {mensaje ? <div className="alert-ok">{mensaje}</div> : null}
      {error ? <div className="alert-error">{error}</div> : null}

      {borrador ? (
        <div className="table-wrap" style={{ marginTop: 14, padding: 14 }}>
          <strong><FiCpu /> Tarea detectada por IA</strong>
          <p className="muted">Campos detectados: {borrador.camposDetectados?.join(', ') || 'ninguno'}</p>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {Object.entries(borrador.datos || {}).map(([campo, valor]) => (
              <label className="field" key={campo}><span>{campo}</span><input value={valor || ''} onChange={(e) => setBorrador((prev) => ({ ...prev, datos: { ...prev.datos, [campo]: e.target.value || null } }))} /></label>
            ))}
          </div>
          <button type="button" className="btn btn-primary" style={{ marginTop: 12 }} onClick={aplicarBorrador}>
            Aplicar tarea al formulario
          </button>
        </div>
      ) : null}

      <div className="form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn" onClick={cerrar}>Cerrar</button>
        <button type="button" className="btn btn-primary" onClick={procesar} disabled={!archivo || cargando || grabando}>
          {cargando ? 'Procesando...' : 'Transcribir y preparar tarea'}
        </button>
      </div>
    </div>
  );
}

export default function Tareas() {
  const [searchParams] = useSearchParams();
  const { usuario } = useAuth();
  const { items, cargando, error, crear, actualizar, eliminar, cargar } = useEntity('tareas');
  const [modal, setModal] = useState(false);
  const [modalVoz, setModalVoz] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [archivosTarea, setArchivosTarea] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [equipo, setEquipo] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [comentario, setComentario] = useState('');
  const [archivosComentario, setArchivosComentario] = useState([]);
  const [enviandoComentario, setEnviandoComentario] = useState(false);
  const [clienteInicial, setClienteInicial] = useState('');
  const [cargandoArchivoId, setCargandoArchivoId] = useState(null);
  const [audioAdjuntoUrls, setAudioAdjuntoUrls] = useState({});
  const audioAdjuntoUrlsRef = useRef(new Set());

  // Edición inline de comentarios
  const [editandoComentarioId, setEditandoComentarioId] = useState(null);
  const [textoEditandoComentario, setTextoEditandoComentario] = useState('');

  // Grabación de audio para tarea
  const [grabandoTask, setGrabandoTask] = useState(false);
  const [segundosTask, setSegundosTask] = useState(0);
  const [audioTaskUrl, setAudioTaskUrl] = useState(null);
  const mediaRecorderTaskRef = useRef(null);
  const streamTaskRef = useRef(null);
  const timerTaskRef = useRef(null);

  // Grabación de audio para comentario
  const [grabandoComment, setGrabandoComment] = useState(false);
  const [segundosComment, setSegundosComment] = useState(0);
  const [audioCommentUrl, setAudioCommentUrl] = useState(null);
  const mediaRecorderCommentRef = useRef(null);
  const streamCommentRef = useRef(null);
  const timerCommentRef = useRef(null);

  const fileInputRef = useRef(null);
  const commentFileInputRef = useRef(null);

  const puedeCrear = puede(usuario, 'tareas', 'crear');
  const puedeEditar = puede(usuario, 'tareas', 'editar');
  const puedeEliminar = puede(usuario, 'tareas', 'eliminar');

  const esAdmin = usuario?.rol === 'superadmin' || usuario?.rol === 'admin';
  const miUsuarioId = String(usuario?._id || usuario?.id || '');

  useEffect(() => {
    api.get('/usuarios').then(({ data }) => setEquipo(data.filter((u) => u.rol !== 'superadmin'))).catch(() => {});
    api.get('/clientes').then(({ data }) => setClientes(Array.isArray(data) ? data : [])).catch(() => {});
    const clienteId = searchParams.get('clienteId') || '';
    const accion = searchParams.get('accion');
    if (clienteId) setClienteInicial(clienteId);
    if (accion) {
      setForm((actual) => ({ ...actual, clienteId }));
      setModal(true);
      if (accion === 'voz' && (window.SpeechRecognition || window.webkitSpeechRecognition)) {
        const Reconocimiento = window.SpeechRecognition || window.webkitSpeechRecognition;
        const reconocimiento = new Reconocimiento();
        reconocimiento.lang = 'es-ES';
        reconocimiento.onresult = (e) => {
          const texto = e.results[0][0].transcript;
          setForm((actual) => ({ ...actual, titulo: texto.slice(0, 200), descripcion: texto }));
        };
        reconocimiento.onerror = () => {};
        reconocimiento.start();
      }
    }
  }, [searchParams]);

  useEffect(() => () => {
    audioAdjuntoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    audioAdjuntoUrlsRef.current.clear();
  }, []);

  const iniciarGrabacionTask = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamTaskRef.current = stream;
      const candidatos = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4'];
      const mimeType = candidatos.find((t) => window.MediaRecorder?.isTypeSupported?.(t));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioTaskUrl(url);
        const file = new File([blob], `audio-tarea-${Date.now()}.webm`, { type: recorder.mimeType || 'audio/webm' });
        setArchivosTarea((prev) => [...prev, file]);
        streamTaskRef.current?.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderTaskRef.current = recorder;
      recorder.start();
      setGrabandoTask(true);
      setSegundosTask(0);
      timerTaskRef.current = setInterval(() => setSegundosTask((s) => s + 1), 1000);
    } catch {
      alert('No se pudo acceder al micrófono. Revisa los permisos del navegador.');
    }
  };

  const detenerGrabacionTask = () => {
    if (mediaRecorderTaskRef.current && mediaRecorderTaskRef.current.state !== 'inactive') {
      mediaRecorderTaskRef.current.stop();
    }
    clearInterval(timerTaskRef.current);
    setGrabandoTask(false);
  };

  const iniciarGrabacionComment = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamCommentRef.current = stream;
      const candidatos = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg', 'audio/mp4'];
      const mimeType = candidatos.find((t) => window.MediaRecorder?.isTypeSupported?.(t));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioCommentUrl(url);
        const file = new File([blob], `audio-comentario-${Date.now()}.webm`, { type: recorder.mimeType || 'audio/webm' });
        setArchivosComentario((prev) => [...prev, file]);
        streamCommentRef.current?.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderCommentRef.current = recorder;
      recorder.start();
      setGrabandoComment(true);
      setSegundosComment(0);
      timerCommentRef.current = setInterval(() => setSegundosComment((s) => s + 1), 1000);
    } catch {
      alert('No se pudo acceder al micrófono. Revisa los permisos del navegador.');
    }
  };

  const detenerGrabacionComment = () => {
    if (mediaRecorderCommentRef.current && mediaRecorderCommentRef.current.state !== 'inactive') {
      mediaRecorderCommentRef.current.stop();
    }
    clearInterval(timerCommentRef.current);
    setGrabandoComment(false);
  };

  const limpiarAudioAdjuntos = () => {
    audioAdjuntoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    audioAdjuntoUrlsRef.current.clear();
    setAudioAdjuntoUrls({});
  };

  const abrirNuevo = () => {
    limpiarAudioAdjuntos();
    setEditando(null);
    setForm({ ...VACIO, clienteId: clienteInicial });
    setArchivosTarea([]);
    setAudioTaskUrl(null);
    setComentario('');
    setArchivosComentario([]);
    setAudioCommentUrl(null);
    setEditandoComentarioId(null);
    setModal(true);
  };

  const abrirUnificado = async (t) => {
    limpiarAudioAdjuntos();
    setEditando(t);
    setForm({
      ...VACIO,
      ...t,
      clienteId: t.clienteId?._id || t.clienteId || '',
      asignadoA: t.asignadoA?._id || t.asignadoA || '',
      fechaTope: t.fechaTope ? t.fechaTope.slice(0, 10) : '',
      diasPreaviso: t.diasPreaviso != null ? t.diasPreaviso : '',
    });
    setArchivosTarea([]);
    setAudioTaskUrl(null);
    setComentario('');
    setArchivosComentario([]);
    setAudioCommentUrl(null);
    setEditandoComentarioId(null);
    setModal(true);

    try {
      const { data } = await api.get(`/tareas/${t._id}`);
      setEditando(data);
    } catch (_) {}
  };

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      if (archivosTarea.length > 0) {
        const formData = new FormData();
        formData.append('titulo', form.titulo);
        formData.append('descripcion', form.descripcion || '');
        formData.append('urgencia', form.urgencia || 'media');
        formData.append('estado', form.estado || 'pendiente');
        if (form.clienteId) formData.append('clienteId', form.clienteId);
        if (form.asignadoA) formData.append('asignadoA', form.asignadoA);
        if (form.fechaTope) formData.append('fechaTope', form.fechaTope);
        if (form.diasPreaviso !== '' && form.diasPreaviso !== null) formData.append('diasPreaviso', form.diasPreaviso);
        Array.from(archivosTarea).forEach((file) => formData.append('archivos', file));

        if (editando) {
          const { data } = await api.put(`/tareas/${editando._id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          setEditando(data);
        } else {
          await api.post('/tareas', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          setModal(false);
        }
      } else {
        const datos = {
          ...form,
          asignadoA: form.asignadoA || null,
          fechaTope: form.fechaTope || null,
          diasPreaviso: form.diasPreaviso !== '' ? Number(form.diasPreaviso) : null,
        };
        if (editando) {
          const { data } = await api.put(`/tareas/${editando._id}`, datos);
          setEditando(data);
        } else {
          await crear(datos);
          setModal(false);
        }
      }
      setArchivosTarea([]);
      setAudioTaskUrl(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const onEliminar = async (t) => {
    if (!window.confirm(`¿Eliminar la tarea "${t.titulo}" y todos sus archivos adjuntos?`)) return;
    try {
      await eliminar(t._id);
      if (editando?._id === t._id) setModal(false);
    } catch { alert('No se pudo eliminar'); }
  };

  const cambiarEstado = async (t, estado) => {
    try {
      const { data } = await actualizar(t._id, { estado });
      if (editando?._id === t._id) {
        setEditando(data);
        setForm((f) => ({ ...f, estado }));
      }
    } catch { alert('No se pudo actualizar'); }
  };

  const enviarComentario = async (e) => {
    e.preventDefault();
    if (!comentario.trim() && archivosComentario.length === 0) return;
    setEnviandoComentario(true);
    try {
      let res;
      if (archivosComentario.length > 0) {
        const formData = new FormData();
        formData.append('texto', comentario);
        Array.from(archivosComentario).forEach((file) => formData.append('archivos', file));
        res = await api.post(`/tareas/${editando._id}/comentarios`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        res = await api.post(`/tareas/${editando._id}/comentarios`, { texto: comentario });
      }
      setEditando(res.data);
      setComentario('');
      setArchivosComentario([]);
      setAudioCommentUrl(null);
      if (commentFileInputRef.current) commentFileInputRef.current.value = '';
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo comentar');
    } finally {
      setEnviandoComentario(false);
    }
  };

  const borrarArchivo = async (archivoId) => {
    if (!window.confirm('¿Eliminar este archivo adjunto del servidor?')) return;
    try {
      const { data } = await api.delete(`/tareas/${editando._id}/archivos/${archivoId}`);
      setEditando(data);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el archivo');
    }
  };

  const obtenerBlobArchivo = async (archivo) => {
    const { data } = await api.get(`/tareas/${editando._id}/archivos/${archivo._id}`, {
      responseType: 'blob',
    });
    return archivo.tipo ? new Blob([data], { type: archivo.tipo }) : data;
  };

  const descargarArchivo = async (archivo) => {
    setCargandoArchivoId(archivo._id);
    try {
      const blob = await obtenerBlobArchivo(archivo);
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = archivo.nombre || 'archivo';
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo descargar el archivo adjunto');
    } finally {
      setCargandoArchivoId(null);
    }
  };

  const cargarAudioAdjunto = async (archivo) => {
    if (audioAdjuntoUrls[archivo._id]) return;
    setCargandoArchivoId(archivo._id);
    try {
      const blob = await obtenerBlobArchivo(archivo);
      const url = URL.createObjectURL(blob);
      audioAdjuntoUrlsRef.current.add(url);
      setAudioAdjuntoUrls((actuales) => ({ ...actuales, [archivo._id]: url }));
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo cargar el audio adjunto');
    } finally {
      setCargandoArchivoId(null);
    }
  };

  const eliminarComentario = async (comentarioId) => {
    if (!window.confirm('¿Eliminar este comentario y sus archivos?')) return;
    try {
      const { data } = await api.delete(`/tareas/${editando._id}/comentarios/${comentarioId}`);
      setEditando(data);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar el comentario');
    }
  };

  const guardarEdicionComentario = async (comentarioId) => {
    if (!textoEditandoComentario.trim()) return;
    try {
      const { data } = await api.put(`/tareas/${editando._id}/comentarios/${comentarioId}`, {
        texto: textoEditandoComentario,
      });
      setEditando(data);
      setEditandoComentarioId(null);
      setTextoEditandoComentario('');
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo editar el comentario');
    }
  };

  const puedeBorrarComentario = (c) => {
    if (esAdmin) return true;
    const autorId = String(c.autor?._id || c.autor || '');
    return autorId === miUsuarioId;
  };

  const puedeBorrarArchivoObj = (a, creadorTareaId, autorComentarioId) => {
    if (esAdmin) return true;
    if (creadorTareaId && String(creadorTareaId) === miUsuarioId) return true;
    if (autorComentarioId && String(autorComentarioId) === miUsuarioId) return true;
    return false;
  };

  const nombreUsuario = (u) => (typeof u === 'object' && u ? `${u.nombre} ${u.apellidos || ''}`.trim() : '—');

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Tareas</h1>
          <p className="page-subtitle">{items.length} tarea(s) · Haz doble clic en cualquier fila para ver detalles completos</p>
        </div>
        {puedeCrear && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" className="btn" onClick={() => setModalVoz(true)}><FiMic /> Tarea por voz</button>
            <button type="button" className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nueva tarea</button>
          </div>
        )}
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="empty"><FiCheckSquare /> No hay tareas.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Tarea</th><th>Asignada a</th><th>Urgencia</th><th>Estado</th><th>Fecha tope</th><th>Preaviso Slack</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr
                  key={t._id}
                  onDoubleClick={() => abrirUnificado(t)}
                  style={{ cursor: 'pointer' }}
                  title="Doble clic para ver detalles, comentarios y adjuntos"
                >
                  <td data-label="Tarea">
                    <strong>{t.titulo}</strong>
                    {(t.comentarios?.length > 0 || t.archivos?.length > 0) && (
                      <span className="sub" style={{ marginLeft: 8, fontSize: '0.8em' }}>
                        {t.comentarios?.length > 0 && `💬 ${t.comentarios.length} `}
                        {t.archivos?.length > 0 && `📎 ${t.archivos.length}`}
                      </span>
                    )}
                  </td>
                  <td data-label="Asignada">{nombreUsuario(t.asignadoA)}</td>
                  <td data-label="Urgencia"><span className={`badge ${URGENCIAS[t.urgencia]}`}>{t.urgencia}</span></td>
                  <td data-label="Estado">
                    {puedeEditar ? (
                      <select className="mini-select" value={t.estado} onClick={(e) => e.stopPropagation()} onChange={(e) => cambiarEstado(t, e.target.value)}>
                        {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    ) : <span className="badge">{ESTADOS[t.estado]}</span>}
                  </td>
                  <td data-label="Fecha tope">{t.fechaTope ? new Date(t.fechaTope).toLocaleDateString('es-ES') : '—'}</td>
                  <td data-label="Preaviso Slack">{t.diasPreaviso != null ? `${t.diasPreaviso} días antes` : '—'}</td>
                  <td className="acciones" onClick={(e) => e.stopPropagation()}>
                    <button className="icon-btn" onClick={() => abrirUnificado(t)} title="Ver / Editar tarea unificada"><FiEye /></button>
                    {puedeEliminar && <button className="icon-btn danger" onClick={() => onEliminar(t)} title="Eliminar"><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL UNIFICADO: Detalle, Edición, Comentarios y Adjuntos */}
      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? `Tarea: ${editando.titulo}` : 'Nueva tarea'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* 1. INFORMACIÓN Y FORMULARIO DE EDICIÓN */}
          <form onSubmit={onGuardar} className="form-grid">
            <h4 className="span-2 permisos-titulo" style={{ marginTop: 0 }}>
              {editando ? 'Información y Configuración de la Tarea' : 'Datos de la nueva tarea'}
            </h4>

            <label className="field span-2">
              <span>Título *</span>
              <input required value={form.titulo} onChange={set('titulo')} disabled={!puedeEditar && !!editando} />
            </label>

            <label className="field span-2">
              <span>Descripción</span>
              <textarea rows={3} value={form.descripcion} onChange={set('descripcion')} disabled={!puedeEditar && !!editando} />
            </label>

            <label className="field span-2">
              <span>Cliente vinculado</span>
              <select value={form.clienteId || ''} onChange={set('clienteId')} disabled={!puedeEditar && !!editando}>
                <option value="">Sin cliente</option>
                {clientes.map((cliente) => <option key={cliente._id} value={cliente._id}>{cliente.nombre}</option>)}
              </select>
            </label>

            <label className="field">
              <span>Urgencia</span>
              <select value={form.urgencia} onChange={set('urgencia')} disabled={!puedeEditar && !!editando}>
                <option value="baja">Baja</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
                <option value="critica">Crítica</option>
              </select>
            </label>

            <label className="field">
              <span>Estado</span>
              <select value={form.estado} onChange={set('estado')} disabled={!puedeEditar && !!editando}>
                {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>

            <label className="field">
              <span>Asignar a</span>
              <select value={form.asignadoA} onChange={set('asignadoA')} disabled={!puedeEditar && !!editando}>
                <option value="">Sin asignar</option>
                {equipo.map((u) => <option key={u._id} value={u._id}>{u.nombre} {u.apellidos}</option>)}
              </select>
            </label>

            <label className="field">
              <span>Fecha tope</span>
              <input type="date" value={form.fechaTope} onChange={set('fechaTope')} disabled={!puedeEditar && !!editando} />
            </label>

            <label className="field span-2">
              <span>Preaviso Slack (días antes)</span>
              <input type="number" min="0" max="365" placeholder="Ej: 3" value={form.diasPreaviso ?? ''} onChange={set('diasPreaviso')} disabled={!puedeEditar && !!editando} />
            </label>

            {/* Añadir adjuntos generales a la tarea */}
            {(puedeEditar || !editando) && (
              <label className="field span-2">
                <span>{editando ? 'Añadir más adjuntos a la tarea' : 'Adjuntar archivos o nota de voz (Archivos máx. 15MB, Audio máx. 70MB)'}</span>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                  <input type="file" multiple ref={fileInputRef} onChange={(e) => setArchivosTarea(Array.from(e.target.files || []))} style={{ flex: 1 }} />
                  <button
                    type="button"
                    className={`btn ${grabandoTask ? 'btn-danger' : ''}`}
                    onClick={grabandoTask ? detenerGrabacionTask : iniciarGrabacionTask}
                  >
                    {grabandoTask ? <><FiStopCircle /> Detener ({segundosTask}s)</> : <><FiMic /> Grabar audio</>}
                  </button>
                </div>
                {audioTaskUrl && (
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <audio controls src={audioTaskUrl} style={{ height: 36 }} />
                    <span className="sub" style={{ fontSize: '0.85em' }}>Audio listo para guardar</span>
                  </div>
                )}
              </label>
            )}

            {(puedeEditar || !editando) && (
              <div className="form-actions span-2" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={guardando}>
                  {guardando ? 'Guardando…' : editando ? 'Guardar cambios de la tarea' : 'Crear tarea'}
                </button>
              </div>
            )}
          </form>

          {/* 2. ADJUNTOS PRINCIPALES DE LA TAREA */}
          {editando && (
            <div style={{ padding: '12px 16px', background: 'var(--bg-card, #f8f9fa)', borderRadius: 8 }}>
              <h4 className="permisos-titulo" style={{ marginTop: 0 }}>
                Archivos adjuntos de la tarea ({editando.archivos?.length || 0})
              </h4>
              {(!editando.archivos || editando.archivos.length === 0) ? (
                <p className="muted" style={{ margin: 0 }}>Sin adjuntos en la tarea.</p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                  {editando.archivos.map((a) => (
                    <li key={a._id} style={{ margin: '8px 0', padding: '6px 10px', background: 'var(--bg-body, #fff)', borderRadius: 6, border: '1px solid var(--border, #eee)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <button type="button" className="btn btn-link" onClick={() => descargarArchivo(a)} disabled={cargandoArchivoId === a._id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                          <FiPaperclip /> {cargandoArchivoId === a._id ? 'Cargando…' : a.nombre}
                          {a.tamaño && <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>({(a.tamaño / (1024 * 1024)).toFixed(2)} MB)</span>}
                        </button>
                        {puedeBorrarArchivoObj(a, editando.creadoPor?._id || editando.creadoPor) && (
                          <button type="button" className="icon-btn danger" onClick={() => borrarArchivo(a._id)} title="Eliminar archivo">
                            <FiTrash2 />
                          </button>
                        )}
                      </div>
                      {esAudio(a) && (
                        <div style={{ marginTop: 6 }}>
                          {audioAdjuntoUrls[a._id] ? (
                            <audio controls src={audioAdjuntoUrls[a._id]} preload="metadata" style={{ height: 36, width: '100%' }} />
                          ) : (
                            <button type="button" className="btn btn-link" onClick={() => cargarAudioAdjunto(a)} disabled={cargandoArchivoId === a._id}>
                              {cargandoArchivoId === a._id ? 'Cargando audio…' : 'Cargar reproductor de audio'}
                            </button>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* 3. HISTORIAL DE COMENTARIOS Y ACTIVIDAD */}
          {editando && (
            <div>
              <h4 className="permisos-titulo" style={{ marginTop: 0 }}>
                Comentarios y Actividad ({editando.comentarios?.length || 0})
              </h4>
              <div className="comentarios" style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(!editando.comentarios || editando.comentarios.length === 0) && (
                  <p className="muted" style={{ margin: 0 }}>No hay comentarios aún en esta tarea.</p>
                )}
                {(editando.comentarios || []).map((c) => {
                  const esAutorComentario = puedeBorrarComentario(c);
                  const estaEditandoEste = editandoComentarioId === c._id;

                  return (
                    <div key={c._id} className="comentario" style={{ padding: 12, background: 'var(--bg-card, #f8f9fa)', borderRadius: 8, border: '1px solid var(--border, #eee)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div>
                          <strong>{nombreUsuario(c.autor)}</strong>
                          <span className="sub" style={{ fontSize: '0.8em', marginLeft: 8 }}>{new Date(c.createdAt).toLocaleString('es-ES')}</span>
                        </div>
                        {esAutorComentario && !estaEditandoEste && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={() => {
                                setEditandoComentarioId(c._id);
                                setTextoEditandoComentario(c.texto);
                              }}
                              title="Editar comentario"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              type="button"
                              className="icon-btn danger"
                              onClick={() => eliminarComentario(c._id)}
                              title="Eliminar comentario"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Texto del comentario o edición inline */}
                      {estaEditandoEste ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                          <textarea
                            rows={2}
                            value={textoEditandoComentario}
                            onChange={(e) => setTextoEditandoComentario(e.target.value)}
                            style={{ width: '100%' }}
                          />
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button type="button" className="btn mini" onClick={() => setEditandoComentarioId(null)}><FiX /> Cancelar</button>
                            <button type="button" className="btn btn-primary mini" onClick={() => guardarEdicionComentario(c._id)}><FiSave /> Guardar</button>
                          </div>
                        </div>
                      ) : (
                        c.texto && <p style={{ margin: '4px 0 0 0', whitespace: 'pre-wrap' }}>{c.texto}</p>
                      )}

                      {/* Adjuntos del comentario */}
                      {c.archivos?.length > 0 && (
                        <div style={{ marginTop: 8, paddingTop: 6, borderTop: '1px dashed var(--border, #ddd)' }}>
                          {c.archivos.map((fa) => (
                            <div key={fa._id} style={{ margin: '4px 0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <button type="button" className="btn btn-link" onClick={() => descargarArchivo(fa)} disabled={cargandoArchivoId === fa._id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.85em' }}>
                                  <FiPaperclip /> {cargandoArchivoId === fa._id ? 'Cargando…' : fa.nombre}
                                  {fa.tamaño && <span style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>({(fa.tamaño / (1024 * 1024)).toFixed(2)} MB)</span>}
                                </button>
                                {puedeBorrarArchivoObj(fa, null, c.autor?._id || c.autor) && (
                                  <button type="button" className="icon-btn danger" onClick={() => borrarArchivo(fa._id)} title="Eliminar este archivo">
                                    <FiTrash2 />
                                  </button>
                                )}
                              </div>
                              {esAudio(fa) && (
                                <div style={{ marginTop: 4 }}>
                                  {audioAdjuntoUrls[fa._id] ? (
                                    <audio controls src={audioAdjuntoUrls[fa._id]} preload="metadata" style={{ height: 32, width: '100%' }} />
                                  ) : (
                                    <button type="button" className="btn btn-link" onClick={() => cargarAudioAdjunto(fa)} disabled={cargandoArchivoId === fa._id}>
                                      {cargandoArchivoId === fa._id ? 'Cargando audio…' : 'Cargar reproductor de audio'}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Formulario de nuevo comentario */}
              <form onSubmit={enviarComentario} className="form-grid" style={{ background: 'var(--bg-card, #f8f9fa)', padding: 12, borderRadius: 8 }}>
                <label className="field span-2">
                  <span>Añadir comentario</span>
                  <input value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Escribe un comentario..." />
                </label>
                <label className="field span-2">
                  <span>Adjuntar archivos o nota de voz al comentario</span>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
                    <input type="file" multiple ref={commentFileInputRef} onChange={(e) => setArchivosComentario(Array.from(e.target.files || []))} style={{ flex: 1 }} />
                    <button
                      type="button"
                      className={`btn ${grabandoComment ? 'btn-danger' : ''}`}
                      onClick={grabandoComment ? detenerGrabacionComment : iniciarGrabacionComment}
                    >
                      {grabandoComment ? <><FiStopCircle /> Detener ({segundosComment}s)</> : <><FiMic /> Grabar voz</>}
                    </button>
                  </div>
                  {audioCommentUrl && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <audio controls src={audioCommentUrl} style={{ height: 36 }} />
                      <span className="sub" style={{ fontSize: '0.85em' }}>Audio listo para enviar</span>
                    </div>
                  )}
                </label>
                <div className="form-actions span-2" style={{ justifyContent: 'flex-end' }}>
                  <button type="submit" className="btn btn-primary" disabled={enviandoComentario || (!comentario.trim() && archivosComentario.length === 0)}>
                    {enviandoComentario ? 'Enviando…' : 'Comentar'}
                  </button>
                </div>
              </form>
            </div>
          )}

        </div>
      </Modal>

      <Modal abierto={modalVoz} onCerrar={() => setModalVoz(false)} titulo="Tarea por voz" ancho={720}>
        <TareaVoz
          clientes={clientes}
          equipo={equipo}
          onCerrar={() => setModalVoz(false)}
          onAplicar={(datos) => {
            setEditando(null);
            setForm({ ...VACIO, ...datos });
            setModalVoz(false);
            setModal(true);
          }}
        />
      </Modal>
    </div>
  );
}
